/**
 * Calendar grid helpers for the Events page.
 *
 * All functions are pure — they operate on the serialized Event shape from the server
 * (startsAt as ISO string) rather than the legacy data.ts shape. Date buckets and
 * labels use the parish timezone so events land on the correct day for every viewer.
 */

import { eventDateParts, eventDay, eventMonth, eventTime } from '@/lib/datetime';

export type EventItem = {
  id: number;
  title: string;
  description: string | null;
  location: string | null;
  category: string | null;
  startsAt: string | null;
  endsAt: string | null;
};

/**
 * Returns a 6×7 grid (42 cells) for the given month/year.
 * Each cell is either null (padding) or a { day, events } object.
 */
export function buildCalendarGrid(
  month: number,
  year: number,
  events: EventItem[],
): (null | { day: number; events: EventItem[] })[] {
  const firstDay = new Date(year, month, 1).getDay(); // 0 = Sunday
  // Adjust so Monday = 0
  const startOffset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (null | { day: number; events: EventItem[] })[] = [];

  // Leading padding
  for (let i = 0; i < startOffset; i++) {
    cells.push(null);
  }

  // Day cells
  for (let day = 1; day <= daysInMonth; day++) {
    const dayEvents = events.filter((e) => {
      if (!e.startsAt) return false;
      const d = eventDateParts(e.startsAt);
      return d.year === year && d.month === month && d.day === day;
    });
    cells.push({ day, events: dayEvents });
  }

  // Trailing padding to complete last row
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

/**
 * Returns events for a specific month/year, sorted chronologically.
 */
export function eventsForMonth(events: EventItem[], month: number, year: number): EventItem[] {
  return events.filter((e) => {
    if (!e.startsAt) return false;
    const d = eventDateParts(e.startsAt);
    return d.year === year && d.month === month;
  });
}

/**
 * Extracts a short date string (day + month name) from an ISO datetime.
 * Returns { day, month, time }.
 */
export function formatEventDate(isoString: string): { day: string; month: string; time: string } {
  return {
    day: eventDay(isoString),
    month: eventMonth(isoString).toUpperCase(),
    time: eventTime(isoString),
  };
}
